import { createFileRoute, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import {
 getLandingPagesDirectory,
 getLandingPagesTotal,
 LANDING_PAGES_PER_DIRECTORY_PAGE,
} from "@/lib/seo-pages.functions";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/landing-pages/directory/$page")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const page = parseInt(params.page, 10);
 if (!Number.isFinite(page) || page < 0) throw notFound();
 const [{ total }, { pages }] = await Promise.all([
 getLandingPagesTotal(),
 getLandingPagesDirectory({ data: { page } }),
 ]);
 if (pages.length === 0) throw notFound();
 return { page, total, pages };
 },
 head: ({ params, loaderData }) => {
 const page = loaderData?.page ?? params.page;
 const title = `Landing Pages, Page ${page} | Heallexa`;
 const description = `Browse Heallexa landing pages, page ${page}. Specialty + location + insurance combinations across the United States.`;
 const url = `${SITE}/landing-pages/directory/${page}`;
 return {
 meta: [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:url", content: url },
 ],
 links: [{ rel: "canonical", href: url }],
 };
 },
 component: LandingDirectoryPage,
});

function LandingDirectoryPage() {
 const { page, total, pages } = Route.useLoaderData();
 const totalPages = Math.ceil(total / LANDING_PAGES_PER_DIRECTORY_PAGE);
 const last = Math.max(totalPages - 1, 0);

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
 <nav className="text-sm text-muted-foreground mb-6">
 <a href="/sitemap" className="hover:text-primary transition-colors">Sitemap</a>
 <span className="mx-2">/</span>
 <a href="/specialties" className="hover:text-primary transition-colors">Specialties</a>
 <span className="mx-2">/</span>
 <a href="/locations" className="hover:text-primary transition-colors">Locations</a>
 <span className="mx-2">/</span>
 <a href="/insurance" className="hover:text-primary transition-colors">Insurance</a>
 <span className="mx-2">/</span>
 <span className="text-foreground">Page {page}</span>
 </nav>

 <header className="mb-8">
 <p className="text-[12px] uppercase tracking-[0.14em] text-muted-foreground tabular-nums">
 {pages.length.toLocaleString()} of {total.toLocaleString()} pages
 </p>
 <h1 className="font-display text-[28px] font-semibold tracking-tight text-foreground mt-2">
 Landing Pages, Page {page}
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground">
 {pages.length.toLocaleString()} of {total.toLocaleString()} pages, grouped by category.
 </p>
 </header>

 {Object.entries(groupByCategory(pages)).map(([category, items]) => (
 <section key={category} className="mb-12">
 <h2 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-4">
 {category} <span className="text-muted-foreground/70 tabular-nums">({items.length})</span>
 </h2>
 <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6">
 {items.map((p: any) => (
 <li key={p.slug} className="min-w-0">
 <a
 href={p.slug}
 className="text-[14px] text-muted-foreground hover:text-primary transition-colors truncate block"
 >
 {p.title ?? p.slug}
 </a>
 </li>
 ))}
 </ul>
 </section>
 ))}

 <nav className="mt-12 flex items-center justify-center gap-3 text-[13px]">
 <a
 href={page > 0 ? `/landing-pages/directory/${page - 1}` : "/landing-pages/directory"}
 className="inline-flex items-center gap-1 h-9 px-3 text-[13px] rounded-md border border-border bg-background hover:bg-muted transition-colors"
 >
 ← Previous
 </a>
 <span className="text-muted-foreground tabular-nums">
 Page {page} of {last}
 </span>
 <a
 href={page < last ? `/landing-pages/directory/${page + 1}` : `/landing-pages/directory/${page}`}
 className="inline-flex items-center gap-1 h-9 px-3 text-[13px] rounded-md border border-border bg-background hover:bg-muted transition-colors"
 >
 Next →
 </a>
 </nav>
 </main>
 <FooterSection />
 </div>
 );
}

const CATEGORY_LABELS: Record<string, string> = {
 specialty_city: "Specialty + Location",
 specialty_state: "Specialty + State",
 specialty_insurance: "Specialty + Insurance",
 specialty_insurance_city: "Specialty + Insurance + Location",
 insurance_city: "Insurance + Location",
 condition: "Conditions",
 service: "Services",
};

function groupByCategory(items: any[]): Record<string, any[]> {
 const grouped: Record<string, any[]> = {};
 for (const item of items) {
 const key = CATEGORY_LABELS[item.page_type] ?? "Other";
 (grouped[key] ||= []).push(item);
 }
 return Object.fromEntries(Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)));
}
